<script setup lang="ts">
import { computed, provide, ref, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { VDefaultsProvider } from 'vuetify/components'
import type { ObjectInventoryField } from '@/entities/Inventory/ObjectTypes'
import { useLoadSubTypesList } from '@/entities/Inventory/ObjectSubTypes/model/api'
import { findObjectSubTypeOption } from '@/entities/Inventory/ObjectSubTypes/model/helpers'
import { InventTypeField } from '@/entities/Inventory/TypeField'
import {
  buildLinkSelectMenuProps,
  linkSelectMenuPropsKey,
} from '@/entities/Inventory/TypeField/model/linkSelectMenuProps'
import { useTranslate } from '@/shared/lib'
import { VModal } from '@/shared/ui'
import { resolveChildPassportLocation } from '../model/passportRuntimeChildRoute'
import {
  fieldHasFollowableRelation,
  parseSelectedObjectListId,
  resolveModalLinkFieldType,
} from '../model/passportRuntimeFieldModal'

const props = withDefaults(
  defineProps<{
    field: ObjectInventoryField | null
    fieldId: string | null
    modelValue: unknown
    /** Подпись текущего catalog из `value_result`; моделью остаётся сырой `value`. */
    displayValue?: string
    label: string
    fieldsLocked?: boolean
  }>(),
  {
    fieldsLocked: false,
    displayValue: '',
  },
)

const emit = defineEmits<{
  apply: [payload: { fieldId: string; value: string; label?: string }]
}>()

const open = defineModel<boolean>('show', { default: false })

const route = useRoute()
const router = useRouter()
const { trs } = useTranslate()
const draftValue = ref<unknown>('')
const linkSelectionLabel = ref('')
const objectId = computed(() => String(route.params.object_id ?? ''))

provide('updateLinkedFieldValue', (_fieldId: string, value: string) => {
  draftValue.value = value
})

/** ObjectLinkSelect сообщает название выбранного объекта — apply label для link-полей. */
provide('updateLinkedFieldLabel', (_fieldId: string, label: string) => {
  linkSelectionLabel.value = label
})

const modalTitle = computed(
  () => props.label?.trim() || trs('itf.passport_runtime_field_select_title'),
)

/**
 * Нормализует поле связи: ObjectLinkSelect ожидает целевой тип в `object_type`
 * (для link-полей приоритет `link_target_type_id` — см. resolveModalLinkFieldType).
 */
const modalField = computed(() => {
  if (!props.field) return null
  const rawValue = props.modelValue == null ? '' : String(props.modelValue)
  const optionId = Number(rawValue)
  const currentOption =
    Number.isFinite(optionId) && optionId > 0 && props.displayValue?.trim()
      ? [{ id: optionId, name: props.displayValue.trim() }]
      : []
  const items = [
    ...currentOption,
    ...(props.field.items ?? []).filter((item) => String(item.id) !== rawValue),
  ]
  const field = resolveModalLinkFieldType(props.field)
  return items.length ? { ...field, items } : field
})

/** Значение редактируется локально и попадает в паспорт только после применения. */
watch(
  () => [open.value, props.modelValue, props.fieldId] as const,
  ([isOpen]) => {
    if (!isOpen) return
    draftValue.value = props.modelValue ?? ''
    linkSelectionLabel.value = ''
  },
  { immediate: true },
)

const canApply = computed(() => Boolean(props.field && props.fieldId) && !props.fieldsLocked)

const modalCatalogTypeId = computed(() => {
  const field = modalField.value
  if (!field) return 0
  const fromObjectType = Number(field.object_type)
  if (Number.isFinite(fromObjectType) && fromObjectType > 0) return fromObjectType
  const fromLink = Number(field.link_target_type_id)
  return Number.isFinite(fromLink) && fromLink > 0 ? fromLink : 0
})

const { data: modalSubTypesData } = useLoadSubTypesList(objectId, modalCatalogTypeId)

const selectedObjectListId = computed(() => parseSelectedObjectListId(draftValue.value))

function resolveApplyLabel(value: string): string {
  if (!value) return ''
  const fromItems = findObjectSubTypeOption(modalField.value?.items ?? [], value)
  if (fromItems?.name?.trim()) return fromItems.name.trim()
  const subTypes = Array.isArray(modalSubTypesData.value?.data?.sub_types)
    ? modalSubTypesData.value.data.sub_types
    : []
  const fromApi = findObjectSubTypeOption(subTypes, value)
  if (fromApi?.name?.trim()) return fromApi.name.trim()
  if (linkSelectionLabel.value.trim()) return linkSelectionLabel.value.trim()
  return props.displayValue?.trim() || ''
}

const relationPassportHref = computed(() => {
  const id = selectedObjectListId.value
  if (!id) return null
  return resolveChildPassportLocation(route, router, id).href
})

const showRelationLink = computed(
  () => fieldHasFollowableRelation(modalField.value) && relationPassportHref.value != null,
)

const closeModal = () => {
  open.value = false
}

const applyValue = () => {
  if (!props.fieldId || !canApply.value) return
  const value = draftValue.value == null ? '' : String(draftValue.value)
  emit('apply', {
    fieldId: props.fieldId,
    value,
    label: resolveApplyLabel(value),
  })
  closeModal()
}

const MODAL_FIELD_OVERLAY_Z = 1_000_010

const modalLinkMenuProps = buildLinkSelectMenuProps({ zIndex: MODAL_FIELD_OVERLAY_Z })
provide(linkSelectMenuPropsKey, modalLinkMenuProps)

const modalFieldDefaults = {
  VSelect: {
    menuProps: modalLinkMenuProps,
  },
  VAutocomplete: {
    menuProps: modalLinkMenuProps,
  },
  VMenu: {
    zIndex: MODAL_FIELD_OVERLAY_Z,
  },
}
</script>

<template>
  <VModal v-model:show="open" :width="520" :title="trs('itf.passport_runtime_field_select_title')">
    <div class="passport-runtime-v2-field-select-modal pt-2">
      <VDefaultsProvider :defaults="modalFieldDefaults">
        <template v-if="modalField">
          <div class="passport-runtime-v2-field-select-modal__field-block">
            <InventTypeField
              v-model="draftValue"
              class="passport-runtime-v2-field-select-modal__field passport-runtime-v2__field-input"
              :type="String(modalField.type)"
              :field-data="modalField"
              :label="modalTitle"
              variant="outlined"
              density="comfortable"
              hide-details
              :readonly="fieldsLocked"
            />

            <a
              v-if="showRelationLink"
              class="passport-runtime-v2-field-select-modal__relation-link"
              :href="relationPassportHref!"
              target="_blank"
              rel="noopener noreferrer"
            >
              {{ trs('itf.passport_runtime_list_action_edit') }}
            </a>
          </div>
        </template>

        <p v-else class="passport-runtime-v2-field-select-modal__empty">
          {{ trs('itf.passport_runtime_empty') }}
        </p>
      </VDefaultsProvider>

      <div class="passport-runtime-v2-field-select-modal__actions">
        <VBtn variant="text" @click="closeModal">
          {{ trs('itf.cancel') }}
        </VBtn>
        <VBtn color="primary" variant="flat" :disabled="!canApply" @click="applyValue">
          {{ trs('itf.apply') }}
        </VBtn>
      </div>
    </div>
  </VModal>
</template>

<style scoped lang="scss">
.passport-runtime-v2-field-select-modal {
  display: flex;
  flex-direction: column;
  gap: 16px;

  &__field-block {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
  }

  &__field {
    width: 100%;
  }

  &__relation-link {
    align-self: flex-start;
    font-size: 13px;
    color: rgb(var(--v-theme-primary));
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  &__empty {
    margin: 0;
    color: rgba(var(--v-theme-on-surface), var(--v-medium-emphasis-opacity));
  }

  &__actions {
    display: flex;
    justify-content: flex-end;
    gap: 8px;
  }
}
</style>
